import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Flame, Trophy, Star, X, HelpCircle } from 'lucide-react';
import { API } from '@/utils/api';

// Badge definitions with unlock requirements
const BADGE_INFO = {
  first_entry: { name: "First Steps", emoji: "✍️", description: "Wrote your first journal entry", howTo: "Write your very first journal entry" },
  streak_7: { name: "Week Warrior", emoji: "🔥", description: "7-day journaling streak", howTo: "Journal every day for 7 days in a row" },
  streak_30: { name: "Monthly Master", emoji: "⭐", description: "30-day journaling streak", howTo: "Journal every day for 30 days in a row" },
  streak_100: { name: "Centurion", emoji: "💎", description: "100-day journaling streak", howTo: "Journal every day for 100 days in a row" },
  entries_10: { name: "Getting Started", emoji: "📝", description: "Wrote 10 journal entries", howTo: "Write a total of 10 journal entries" },
  entries_50: { name: "Dedicated Writer", emoji: "📚", description: "Wrote 50 journal entries", howTo: "Write a total of 50 journal entries" },
  all_moods: { name: "Emotional Explorer", emoji: "🌈", description: "Used all mood types", howTo: "Use every available mood at least once" },
  first_voice: { name: "Voice Pioneer", emoji: "🎤", description: "Used voice typing", howTo: "Use the voice typing feature to write an entry" },
};

const allBadgeIds = ['first_entry', 'streak_7', 'streak_30', 'streak_100', 'entries_10', 'entries_50', 'all_moods', 'first_voice'];

// Same sparkle effect as New Entry button
const createSparkleEffect = (event) => {
  const sparkles = ['✨', '⭐', '💫', '🌟', '✦', '★'];
  const colors = ['color-pink', 'color-orange', 'color-purple', 'color-gold', 'color-cyan', 'color-white'];
  
  const container = document.createElement('div');
  container.className = 'sparkle-container';
  container.style.left = `${event.clientX}px`;
  container.style.top = `${event.clientY}px`;
  document.body.appendChild(container);

  // Create expanding rings
  for (let i = 1; i <= 3; i++) {
    const ring = document.createElement('div');
    ring.className = `sparkle-ring ring-${i}`;
    container.appendChild(ring);
  }

  // Create many sparkle particles - outer burst
  for (let i = 0; i < 20; i++) {
    const particle = document.createElement('span');
    particle.className = `sparkle-particle ${colors[Math.floor(Math.random() * colors.length)]}`;
    particle.textContent = sparkles[Math.floor(Math.random() * sparkles.length)];
    
    const angle = (i / 20) * 360 + (Math.random() * 20 - 10);
    const distance = 80 + Math.random() * 100;
    const tx = Math.cos(angle * Math.PI / 180) * distance;
    const ty = Math.sin(angle * Math.PI / 180) * distance;
    
    particle.style.setProperty('--tx', `${tx}px`);
    particle.style.setProperty('--ty', `${ty}px`);
    particle.style.animationDelay = `${Math.random() * 0.15}s`;
    particle.style.fontSize = `${1.5 + Math.random() * 1}rem`;
    
    container.appendChild(particle);
  }

  // Inner sparkles
  for (let i = 0; i < 10; i++) {
    const particle = document.createElement('span');
    particle.className = `sparkle-particle ${colors[Math.floor(Math.random() * colors.length)]}`;
    particle.textContent = sparkles[Math.floor(Math.random() * sparkles.length)];
    
    const angle = (i / 10) * 360;
    const distance = 30 + Math.random() * 40;
    const tx = Math.cos(angle * Math.PI / 180) * distance;
    const ty = Math.sin(angle * Math.PI / 180) * distance;
    
    particle.style.setProperty('--tx', `${tx}px`);
    particle.style.setProperty('--ty', `${ty}px`);
    particle.style.animationDelay = `${0.05 + Math.random() * 0.1}s`;
    particle.style.fontSize = `${1 + Math.random() * 0.8}rem`;
    
    container.appendChild(particle);
  }

  // Play a magical sound
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const frequencies = [523, 659, 784, 1047, 1319];
    frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + index * 0.04);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + index * 0.04 + 0.02);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + index * 0.04 + 0.25);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start(audioContext.currentTime + index * 0.04);
      oscillator.stop(audioContext.currentTime + index * 0.04 + 0.35);
    });
  } catch (e) {
    console.log('Audio not available');
  }

  // Remove container after animation
  setTimeout(() => container.remove(), 1100);
};

// Inject CSS for animated glow and themed badges
const glowStyles = `
@keyframes badgeGlow {
  0%, 100% {
    box-shadow: 0 0 5px var(--theme-primary), 0 0 10px var(--theme-primary), 0 0 15px var(--theme-secondary);
    border-color: var(--theme-primary);
  }
  50% {
    box-shadow: 0 0 10px var(--theme-secondary), 0 0 20px var(--theme-secondary), 0 0 30px var(--theme-primary);
    border-color: var(--theme-secondary);
  }
}
.badge-glow {
  animation: badgeGlow 2s ease-in-out infinite;
  border: 2px solid var(--theme-primary);
}
.journal-today-badge-pending {
  background-color: rgba(var(--theme-primary-rgb), 0.2);
  color: var(--theme-primary);
  border: 2px solid var(--theme-border);
}
.journal-today-badge-done {
  background-color: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.3);
}
`;

// Add styles to document
if (typeof document !== 'undefined' && !document.getElementById('badge-glow-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'badge-glow-styles';
  styleSheet.textContent = glowStyles;
  document.head.appendChild(styleSheet);
}

// Play celebration sound - full arpeggio with sparkle
const playCelebrationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99, 1046.50];
    
    notes.forEach((freq, i) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      const startTime = audioContext.currentTime + (i * 0.12);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.25, startTime + 0.03);
      gainNode.gain.linearRampToValueAtTime(0, startTime + 0.25);
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.25);
    });
    
    setTimeout(() => {
      const sparkle = audioContext.createOscillator();
      const sparkleGain = audioContext.createGain();
      sparkle.connect(sparkleGain);
      sparkleGain.connect(audioContext.destination);
      sparkle.frequency.value = 1500;
      sparkle.type = 'sine';
      sparkleGain.gain.setValueAtTime(0.15, audioContext.currentTime);
      sparkleGain.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.15);
      sparkle.start();
      sparkle.stop(audioContext.currentTime + 0.15);
    }, 500);
  } catch (e) {
    console.log('Audio not available');
  }
};

// Streak display component for dashboard
export const StreakDisplay = () => {
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Small delay to stagger from other initial requests
    const timer = setTimeout(() => {
      fetchStreak();
    }, 300);
    
    // Listen for custom event to refresh streaks (triggered after entry save)
    const handleRefresh = () => {
      console.log('[Streaks] Refresh triggered');
      fetchStreak();
    };
    
    window.addEventListener('refreshBadges', handleRefresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('refreshBadges', handleRefresh);
    };
  }, []);

  const fetchStreak = async () => {
    try {
      const response = await fetch(`${API}/user/streak`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStreakData(data);
      }
    } catch (error) {
      // Silently fail - streak will just not display
      console.debug('Streak fetch failed:', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-4 mb-6 animate-pulse">
        <div className="h-16 rounded-xl" style={{ backgroundColor: 'var(--theme-background-secondary)' }} />
      </div>
    );
  }

  if (!streakData) return null;

  return (
    <div className="glass-card p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div 
              className="p-2 rounded-xl"
              style={{ backgroundColor: streakData.current_streak > 0 ? 'var(--theme-primary)' : 'var(--theme-background-secondary)' }}
            >
              <Flame className={`w-6 h-6 ${streakData.current_streak > 0 ? 'text-white' : 'text-[var(--theme-text)]/50'}`} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>{streakData.current_streak}</p>
              <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Day Streak</p>
            </div>
          </div>
          <div className="w-px h-12" style={{ backgroundColor: 'var(--theme-border)', opacity: 0.2 }} />
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl" style={{ backgroundColor: 'var(--theme-background-secondary)' }}>
              <Trophy className="w-5 h-5 text-[var(--theme-primary)]" />
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: 'var(--theme-text)' }}>{streakData.longest_streak}</p>
              <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Best Streak</p>
            </div>
          </div>
          <div className="w-px h-12 hidden sm:block" style={{ backgroundColor: 'var(--theme-border)', opacity: 0.2 }} />
          <div className="hidden sm:flex items-center gap-2">
            <div className="p-2 rounded-xl" style={{ backgroundColor: 'var(--theme-background-secondary)' }}>
              <Star className="w-5 h-5 text-[var(--theme-primary)]" />
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: 'var(--theme-text)' }}>{streakData.total_entries}</p>
              <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Total Entries</p>
            </div>
          </div>
        </div>
        <div 
          className={`px-3 py-1.5 rounded-full text-xs font-medium ${
            streakData.journaled_today 
              ? 'journal-today-badge-done' 
              : 'journal-today-badge-pending'
          }`}
        >
          {streakData.journaled_today ? '✓ Journaled Today' : 'Journal Today!'}
        </div>
      </div>
    </div>
  );
};

// Badge Celebration Modal - reusable for click and new badge
const BadgeCelebrationModal = ({ badge, onClose }) => {
  useEffect(() => {
    playCelebrationSound();
  }, []);

  // Use portal to render at document body level, avoiding any container clipping
  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      style={{ zIndex: 99999 }}
    >
      <div 
        className="rounded-3xl p-8 max-w-md mx-4 text-center relative overflow-hidden"
        style={{ 
          backgroundColor: 'var(--theme-background)',
          border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
        }}
      >
        {/* Sparkle effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={`sparkle-${i}`}
              className="absolute animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random()}s`,
              }}
            >
              ✨
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--theme-background-secondary)] transition-colors z-10"
        >
          <X className="w-5 h-5" style={{ color: 'var(--theme-text-secondary)' }} />
        </button>

        <div className="text-6xl mb-4 animate-bounce">🎉</div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>Badge Unlocked!</h2>
        
        <div className="rounded-2xl p-6 mt-4" style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.8 }}>
          <div className="text-6xl mb-3">{badge.emoji}</div>
          <h3 className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>{badge.name}</h3>
          <p className="mt-2" style={{ color: 'var(--theme-text-secondary)' }}>{badge.description}</p>
        </div>

        <button
          onClick={onClose}
          className="mt-6 px-8 py-3 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
          style={{ background: 'var(--theme-button-bg)' }}
        >
          Awesome!
        </button>
      </div>
    </div>,
    document.body
  );
};

// Badges display component
export const BadgesDisplay = ({ compact = false, onBadgeUnlocked }) => {
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const [showBadgeInfo, setShowBadgeInfo] = useState(false);
  const [newBadges, setNewBadges] = useState([]);
  const [celebrationQueue, setCelebrationQueue] = useState([]);

  useEffect(() => {
    // Small delay to stagger from other initial requests
    const timer = setTimeout(() => {
      fetchBadges();
    }, 600);
    
    // Listen for custom event to refresh badges (triggered after entry save)
    const handleRefresh = () => {
      console.log('[Badges] Refresh triggered');
      fetchBadges();
    };
    
    window.addEventListener('refreshBadges', handleRefresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('refreshBadges', handleRefresh);
    };
  }, []);
  
  // Handle celebration queue - show badges one by one
  useEffect(() => {
    console.log('[Badges] Celebration queue effect:', { 
      queueLength: celebrationQueue.length, 
      showCelebration,
      currentBadge: celebrationBadge?.name 
    });
    
    if (celebrationQueue.length > 0 && !showCelebration) {
      const nextBadge = celebrationQueue[0];
      console.log('[Badges] Triggering celebration for:', nextBadge.name);
      // Small delay to ensure state is ready
      setTimeout(() => {
        setCelebrationBadge(nextBadge);
        setShowCelebration(true);
      }, 100);
    }
  }, [celebrationQueue, showCelebration]);

  // Removed duplicate useEffect - sound now plays from BadgeCelebrationModal

  const fetchBadges = async () => {
    try {
      const response = await fetch(`${API}/user/badges`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Badges] Fetched badge data:', { 
          earned: data.badges?.length || 0, 
          new_badges: data.new_badges?.length || 0,
          new_badge_names: data.new_badges?.map(b => b.name) || []
        });
        setBadgeData(data);
        
        // Show celebration for new badges - queue them up
        if (data.new_badges && data.new_badges.length > 0) {
          console.log('[Badges] New badges found, adding to celebration queue:', data.new_badges.map(b => b.name));
          setNewBadges(data.new_badges.map(b => b.id));
          // Add all new badges to the celebration queue
          setCelebrationQueue(prev => {
            // Avoid duplicates - only add badges not already in queue
            const existingIds = prev.map(b => b.id);
            const newToAdd = data.new_badges.filter(b => !existingIds.includes(b.id));
            console.log('[Badges] Adding to queue:', newToAdd.map(b => b.name));
            return [...prev, ...newToAdd];
          });
          // Notify parent if callback provided
          if (onBadgeUnlocked) {
            onBadgeUnlocked(data.new_badges);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCelebrationClose = async () => {
    setShowCelebration(false);
    setCelebrationBadge(null);
    
    // Remove the first badge from queue and clear its celebration flag
    setCelebrationQueue(prev => {
      const remaining = prev.slice(1);
      console.log('[Badges] Celebration closed, remaining in queue:', remaining.length);
      return remaining;
    });
    
    // Clear celebration flag on server
    try {
      await fetch(`${API}/user/badges/celebrate`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Failed to clear celebration:', error);
    }
  };

  const handleBadgeClick = (badgeId, earned) => {
    if (earned) {
      // Replay celebration for earned badge
      const badgeInfo = BADGE_INFO[badgeId];
      setCelebrationBadge({ ...badgeInfo, id: badgeId });
      setShowCelebration(true);
    } else {
      // Show how to unlock for locked badge
      setShowBadgeInfo(true);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-4 animate-pulse">
        <div className="h-20 rounded-xl" style={{ backgroundColor: 'var(--theme-background-secondary)' }} />
      </div>
    );
  }

  if (!badgeData) return null;

  const earnedIds = badgeData.badges.map(b => b.id);

  return (
    <>
      {/* Badge celebration modal */}
      {showCelebration && celebrationBadge && (
        <BadgeCelebrationModal 
          badge={celebrationBadge} 
          onClose={handleCelebrationClose} 
        />
      )}

      {/* Badge Info Modal - How to unlock */}
      {showBadgeInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div 
            className="rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            style={{ 
              backgroundColor: 'var(--theme-background)',
              border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
            }}
          >
            <div className="flex items-center justify-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
                <Trophy className="w-6 h-6 text-[var(--theme-primary)]" />
                How to Earn Badges
              </h2>
            </div>
            <button
              onClick={() => setShowBadgeInfo(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--theme-background-secondary)] transition-colors"
            >
              <X className="w-5 h-5" style={{ color: 'var(--theme-text-secondary)' }} />
            </button>
            
            <div className="space-y-3">
              {Object.entries(BADGE_INFO).map(([id, badge]) => {
                const earned = earnedIds.includes(id);
                return (
                  <div 
                    key={id} 
                    className="p-3 rounded-xl text-center"
                    style={{ backgroundColor: earned ? 'var(--theme-background-secondary)' : 'rgba(0,0,0,0.2)', opacity: earned ? 0.8 : 1 }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className={`text-3xl ${earned ? '' : 'grayscale opacity-50'}`}>
                        {badge.emoji}
                      </span>
                      <div>
                        <div className="flex items-center justify-center gap-2">
                          <h3 className="font-semibold" style={{ color: earned ? 'var(--theme-text)' : 'var(--theme-text-secondary)' }}>
                            {badge.name}
                          </h3>
                          {earned && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                              ✓ Earned
                            </span>
                          )}
                        </div>
                        <p className="text-sm mt-1" style={{ color: 'var(--theme-text-secondary)' }}>{badge.howTo}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <button
              onClick={() => setShowBadgeInfo(false)}
              className="mt-6 w-full py-3 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
              style={{ background: 'var(--theme-button-bg)' }}
            >
              Got It!
            </button>
          </div>
        </div>
      )}

      {/* Badges section */}
      <div className={`glass-card p-4 ${compact ? '' : 'mb-6'}`}>
        <div className="flex items-center justify-center mb-4">
          <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <Trophy className="w-5 h-5 text-[var(--theme-primary)]" />
            Badges
            <span className="text-xs ml-2" style={{ color: 'var(--theme-text-secondary)' }}>
              {badgeData.badges.length} / {badgeData.total_possible}
            </span>
          </h3>
          <button
            onClick={() => setShowBadgeInfo(true)}
            className="ml-2 p-1.5 rounded-lg hover:bg-[var(--theme-background-secondary)]/50 transition-colors"
            title="How to earn badges"
          >
            <HelpCircle className="w-4 h-4 text-[var(--theme-primary)]" />
          </button>
        </div>

        <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-4 sm:grid-cols-8'} gap-3`}>
          {allBadgeIds.map((badgeId) => {
            const earned = badgeData.badges.find(b => b.id === badgeId);
            const isNew = newBadges.includes(badgeId);
            const badgeInfo = BADGE_INFO[badgeId];
            const isSpecial = badgeInfo?.special;
            
            return (
              <div
                key={badgeId}
                onClick={(e) => {
                  handleBadgeClick(badgeId, !!earned);
                  if (earned) createSparkleEffect(e);
                }}
                className={`relative p-3 rounded-xl text-center transition-all cursor-pointer ${
                  earned 
                    ? 'hover:scale-110 badge-glow' 
                    : 'opacity-60 hover:opacity-80 hover:scale-105 border border-transparent'
                } ${isNew ? 'ring-2 ring-yellow-400 ring-offset-2' : ''}`}
                style={{ 
                  backgroundColor: earned && isSpecial ? 'rgba(255, 215, 0, 0.15)' : 'var(--theme-background-secondary)',
                  border: earned && isSpecial ? '2px solid #FFD700' : undefined,
                  boxShadow: earned && isSpecial ? '0 0 20px rgba(255, 215, 0, 0.3)' : undefined,
                  ringOffsetColor: 'var(--theme-background)'
                }}
              >
                <div className={`text-2xl ${earned ? '' : 'grayscale'}`}>
                  {earned ? earned.emoji : '🔒'}
                </div>
                {!compact && (
                  <p 
                    className="text-[10px] mt-1 truncate" 
                    style={{ 
                      color: earned && isSpecial ? '#FFD700' : 'var(--theme-text-secondary)',
                      fontWeight: earned && isSpecial ? '600' : undefined
                    }}
                  >
                    {earned ? earned.name : '???'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default StreakDisplay;
